import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import Decimal from 'decimal.js';

import { getOrderDetail } from '../orderDetailActions';
import { getAccountDetailsApi } from '../../../../api/order';
import logo from '../../../../assets/images/logo_new.png'
import useAuthDataStore from '../../../../hooks/store/useAuthDataStore';
import { getRetailerBillingAddressSvc } from '../../../../services/retailer';
import { getRetailerInfoSvc } from '../../../../services/retailer';
import { getOrderDetailApi } from '../../../../api/order';
import { showCustomToast } from '../../../../controls/CustomToast';
import { formatDate, formatDateTime } from '../../../../utils/formatDate';
import './taxInvoicePage.scss';

const TaxInvoicePage = (props) => {
    const dispatch = useDispatch();
    const [billingInfo, setBillingInfo] = useState(null);
    const [orderDetail, setOrderDetail] = useState(null);
    const [abn, setAbn] = useState('');
    const { retailerId } = useAuthDataStore.getState();
    const { orderId } = useParams();

    useEffect(() => {
        if (orderId) {
            getOrderDetailApi({ id: orderId })
                .then((res) => {
                    const detail = Array.isArray(res.result)
                        ? res.result[0]
                        : res.result;
                    setOrderDetail(detail);
                })

            getRetailerBillingAddressSvc(retailerId)
                .then((res) => {
                    if (res.success) {
                        setBillingInfo(res.data);
                    } else {
                        showCustomToast(
                            'Error, Please try again later.'
                        );
                    }
                }).catch((err) => {
                    showCustomToast(
                        'Error, Please try again later.'
                    );
                });

            getRetailerInfoSvc(retailerId)
                .then((res) => {
                    if (res.success) {
                        setAbn(res.data.rs_abn);
                    } else {
                        showCustomToast(
                            'Error, Please try again later.'
                        );
                    }
                }).catch((err) => {
                    showCustomToast(
                        'Error, Please try again later.'
                    );
                });
        }
    }, [orderId, retailerId]);

    useEffect(() => {
        if (orderDetail && billingInfo) {
            requestAnimationFrame(() => {
                setTimeout(() => {
                    window.print();
                }, 500);
            });
        }
    }, [orderDetail, billingInfo]);

    const formatCurrency = (amount) => {
        const decimalAmount = new Decimal(amount);
        return new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: 'AUD'
        }).format(decimalAmount.toFixed(2));
    };


    const calculateGST = (amount) => {
        const gst = new Decimal(amount).div(11);
        return gst.toDecimalPlaces(2);
    }

    const getTotalGSTAmount = (order) => {

        const nzCutoffDate = process.env.REACT_APP_NZ_GST_CUTOFF_TIME
        const orderDate = new Date(order.order_date).getTime();
        const cutoffDate = new Date(nzCutoffDate).getTime();
        if(order.country === "NZ" && orderDate > cutoffDate) {
            return calculateGST(order.processing_fee);
        }
        const grandTotalInclGST = getGrandTotalInclGST(order);
        return calculateGST(grandTotalInclGST);
    };

    const getItemsGSTSum = (orders) => {
        let itemsGSTSum = new Decimal(0);
        orders.items.forEach((item) => {
            itemsGSTSum = itemsGSTSum.plus(calculateGST(item.sub_total_inc_shipping));
        });
        if (orders.processing_fee > 0) {
            itemsGSTSum = itemsGSTSum.plus(calculateGST(orders.processing_fee));
        }
        return itemsGSTSum;
    };

    const getAdjustedGST = (itemAmount, isLastItem, order) => {
        const totalGSTAmount = getTotalGSTAmount(order);
        const itemsGSTSum = getItemsGSTSum(order);
        const gstDifference = totalGSTAmount.minus(itemsGSTSum);

        if (isLastItem && gstDifference.abs().gte(0.01)) {
            return calculateGST(itemAmount).plus(gstDifference);
        }
        return calculateGST(itemAmount);
    };

    const getGrandTotalInclGST = (order) => {
        let grandTotal = new Decimal(0);
        order.items.forEach((item) => {
            grandTotal = grandTotal.plus(item.sub_total_inc_shipping);
        });
        grandTotal = grandTotal.plus(order.processing_fee);
        return grandTotal;
    }

    const getOrderItemGST = (order, isLastItem, itemAmount) => {
        const nzCutoffDate = process.env.REACT_APP_NZ_GST_CUTOFF_TIME
        const orderDate = new Date(order.order_date).getTime();
        const cutoffDate = new Date(nzCutoffDate).getTime();
        if(order.country === "NZ" && orderDate > cutoffDate) {
            return 'Free';
        }
        return formatCurrency(getAdjustedGST(itemAmount, isLastItem, orderDetail));
    }



    if (!orderDetail || !billingInfo || !abn || orderDetail.status === 'unpaid' || orderDetail.status === 'cancelled') {
        return null;
    }
    return (
        <div className="tax-invoice-page">
            {/* Tax Invoice Header */}
            <div className="tax-invoice-header">
                <div className="invoice-info">
                    <h1 className="invoice-title">Tax Invoice</h1>
                    <div className="invoice-info-header">Invoice No: <span className="invoice-number"></span>{orderDetail.increment_id}</div>
                    <div className="invoice-info-header">Invoice Date: <span className="invoice-number"></span>{formatDate(orderDetail.order_date)}</div>
                </div>
                <div className="header-logo">
                    <img
                        src={logo}
                        className="logo"
                        alt="DROPSHIPZONE"
                    />
                </div>
            </div>

            <div className="tax-invoice-content">
                {/* Two Column Layout - Invoice To & From */}
                <div className="col2-set">
                    <div className="column-1">
                        <div className='detail-header'>Invoice To</div>
                        <address>
                            {billingInfo.company}<br />
                            ABN {abn}<br />
                            {billingInfo.street}<br />
                            {billingInfo.suburb + ' ' + billingInfo.state + ' ' + billingInfo.postcode}<br />
                            {billingInfo.country === 'AU' ? 'Australia' : billingInfo.country}
                        </address>
                    </div>
                    <div className="column-2">
                        <div className='detail-header'>Invoice From</div>
                        <address>
                            New Aim PTY LTD<br />
                            ABN 50 115 804 432<br />
                            16-18 CATO ST<br />
                            HAWTHORN EAST VIC 3123<br />
                            Australia
                        </address>
                    </div>
                </div>

                {/* Items Table */}
                <div className="items-section">
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Dropshipzone Order ID</th>
                                    <th>Product Name</th>
                                    <th>SKU</th>
                                    <th className="a-right">Unit Price</th>
                                    <th className="a-right">Shipping Per Item</th>
                                    <th className="a-right">Qty</th>
                                    <th className="a-right">Amount AUD</th>
                                    <th className="a-right">GST</th>
                                    <th className="a-right">Order Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orderDetail.items && orderDetail.items.map((item, index) => {
                                    const isLastItem = index === orderDetail.items.length - 1;
                                    return (
                                        <tr key={index}>
                                            <td>
                                                {index == 0 && <span className="item-data-text">{orderDetail.increment_id}</span>}
                                            </td>
                                            <td className="product-name-cell">
                                                <span className="item-data-text product-name-text">{item.name}</span>
                                            </td>
                                            <td className="a-left">
                                                <span className="item-data-text">{item.sku}</span>
                                            </td>
                                            <td className="a-right">
                                                <span className="item-data-text">{formatCurrency(item.price)}</span>
                                            </td>
                                            <td className="a-right">
                                                <span className="item-data-text">{formatCurrency(item.shipping_fee)}</span>
                                            </td>
                                            <td className="a-right">
                                                <span className="item-data-text">{item.qty}</span>
                                            </td>
                                            <td className="a-right">
                                                <span className="item-data-text">{formatCurrency(item.sub_total_inc_shipping)}</span>
                                            </td>
                                            <td className="a-right">
                                                <span className="item-data-text">{getOrderItemGST(orderDetail, isLastItem, item.sub_total_inc_shipping)}</span>
                                            </td>
                                            <td className="a-right">
                                                <span className="item-data-text">{formatDateTime(orderDetail.order_date)}</span>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {/* Processing fee row */}
                                {orderDetail.processing_fee > 0 && (
                                    <tr>
                                        <td></td>
                                        <td>
                                            <span className="item-data-text">Processing fee - PayPal</span>
                                        </td>
                                        <td></td>
                                        <td className="a-right">
                                            <span className="item-data-text">{formatCurrency(orderDetail.processing_fee)}</span>
                                        </td>
                                        <td></td>
                                        <td className="a-right">
                                            <span className="item-data-text">1</span>
                                        </td>
                                        <td className="a-right">
                                            <span className="item-data-text">{formatCurrency(orderDetail.processing_fee)}</span>
                                        </td>
                                        <td className="a-right">
                                            <span className="item-data-text">{formatCurrency(calculateGST(orderDetail.processing_fee))}</span>
                                        </td>
                                        <td className="a-right">
                                            <span className="item-data-text">{formatDateTime(orderDetail.order_date)}</span>
                                        </td>
                                    </tr>
                                )}
                                <tr className="price-summary-text">
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td className="a-left nobr">Grand Total (incl. GST)</td>
                                    <td></td>
                                    <td></td>
                                    <td className="a-right">{formatCurrency(getGrandTotalInclGST(orderDetail))}</td>
                                </tr>
                                <tr className="price-summary-text">
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td className="a-left nobr">GST</td>
                                    <td></td>
                                    <td></td>
                                    <td className="a-right">{formatCurrency(getTotalGSTAmount(orderDetail))}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="note-text">*Export sale - GST-free under s38-185 of the GST Act.</div>
                <div className="buttons-set">
                    <button
                        type="button"
                        title="Close Window"
                        className="button close-button"
                        onClick={() => window.close()}
                    >
                        <span>Close Window</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TaxInvoicePage;
